/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/no-noninteractive-tabindex */
import React from 'react';
import PropTypes from 'prop-types';
import { keyCode as KEY_CODES } from '@bingads-webui/keycode';
import { uniqueId, noop, first, last } from 'underscore';
import { getTabbableNodes, getFrontGuards, addDataFocus, removeDataFocus, focusAsync } from './focus-utils';

export class FocusLock extends React.PureComponent {
  constructor(props) {
    super(props);

    this.rootRef = React.createRef();
    this.anchor = null;
    this.focusId = uniqueId('focus_id_');
    this.firstFocusNode = null;
    this.lastFocusNode = null;
    this.isFocusLockStart = false;
  }

  componentDidMount() {
    // if not customized trigger focus, will trigger it when did mount component
    if (!this.props.isControlledFocusLock) {
      this.anchor = document && document.activeElement;
      this.focusOneItemOnMount();
    }
  }

  componentDidUpdate(prevProps) {
    const {
      isControlledFocusLock,
      isFocusLock,
    } = this.props;

    if (prevProps.isControlledFocusLock === isControlledFocusLock
        && prevProps.isFocusLock === isFocusLock) {
      return;
    }

    if (isControlledFocusLock) {
      if (isFocusLock) {
        this.anchor = document && document.activeElement;
        this.focusOneItemOnMount();
      } else {
        removeDataFocus(this.rootRef.current);
        this.returnFocus();
        this.isFocusLockStart = false;
      }
    }
  }

  componentWillUnmount() {
    if (!this.props.isControlledFocusLock) {
      this.returnFocus();
    }
    this.anchor = null;
    this.firstFocusNode = null;
    this.lastFocusNode = null;
    this.focusId = null;
  }

  onFocusFront = (event) => {
    event.preventDefault();
    event.stopPropagation();
    /* istanbul ignore else */
    this.setFirstFocusNode();
    if (this.firstFocusNode) {
      this.firstFocusNode.focus();
    }
  };

  // onFocus Back is only handled for front guard focusable situation
  onFocusBack = (event) => {
    event.preventDefault();
    event.stopPropagation();
    // skip the first time to focus back when initial focus lock
    /* istanbul ignore else */
    this.setLastFocusNode();
    if (this.lastFocusNode && this.isFocusLockStart) {
      this.lastFocusNode.focus();
    } else if (this.lastFocusNode) {
      this.isFocusLockStart = true;
    }
  };

  setFirstFocusNode = () => {
    const tabbaleNodes = getTabbableNodes(this.rootRef.current);
    this.firstFocusNode = (tabbaleNodes && tabbaleNodes.length > 0) ? first(tabbaleNodes) : null;
  };

  setLastFocusNode = () => {
    const tabbaleNodes = getTabbableNodes(this.rootRef.current);
    this.lastFocusNode = (tabbaleNodes && tabbaleNodes.length > 0) ? last(tabbaleNodes) : null;
  };

  focusOneItemOnMount = () => {
    const {
      skipFocusOnMount,
      focusFirstItemOnMount,
    } = this.props;
    if (!skipFocusOnMount) {
      this.setFirstFocusNode();

      if (focusFirstItemOnMount && this.firstFocusNode) {
        focusAsync(this.firstFocusNode, () => addDataFocus(this.rootRef.current));
        this.isFocusLockStart = true;
      } else {
        this.focusFrontGuard();
      }
    } else {
      this.isFocusLockStart = true;
    }
  };

  focusFrontGuard = () => {
    const frontGuard = getFrontGuards(this.rootRef.current, this.focusId);
    focusAsync(frontGuard, () => addDataFocus(this.rootRef.current));
  };

  returnFocus = () => {
    if (this.props.getAnchor && this.props.getAnchor()) {
      // replace the anchor if set it outside
      this.anchor = this.props.getAnchor();
    }
    focusAsync(this.anchor, null, true);
  };

  focusNextElement = (currentElement) => {
    const tabbableElements = getTabbableNodes(this.rootRef.current);
    const currentIndex = tabbableElements.indexOf(currentElement);

    if (currentIndex !== -1 && currentIndex < tabbableElements.length - 1) {
      tabbableElements[currentIndex + 1].focus();
    } else if (currentIndex === tabbableElements.length - 1) {
      tabbableElements[0].focus();
    }
  }

  exitHandler = (event) => {
    if (event.which === KEY_CODES.Escape) {
      this.props.onExit(event);
    }
  };

  render() {
    const {
      className,
      children,
      lockRegionAriaLabel,
    } = this.props;

    return (
      <div className={className} ref={this.rootRef} onKeyDown={this.exitHandler}>
        <div id={`guard-front-${this.focusId}`} data-focus-guard tabIndex={0} aria-label={lockRegionAriaLabel} onFocus={this.onFocusBack} role="region" />
        {children}
        <div id={`guard-tail-${this.focusId}`} data-focus-guard tabIndex={0} onFocus={this.onFocusFront} />
      </div>
    );
  }
}

FocusLock.propTypes = {
  className: PropTypes.string,
  /**
   * The children for Panel, also the content for the panel
   */
  children: PropTypes.node.isRequired,
  /**
   * function to exit focus trap
   */
  onExit: PropTypes.func,
  /**
   * flag for user to enable focus trigger outside
   * This will help those show/hide situation, e.g. dropdown menu
   */
  isControlledFocusLock: PropTypes.bool,
  /**
   * this flag will only useful when isControlledFocusLock is enabled
   * this flag will indicate focus lock status
   */
  isFocusLock: PropTypes.bool,
  /**
   * ready a11 text for some components, e.g. panel
   */
  lockRegionAriaLabel: PropTypes.string,
  /**
   * use this flag to enable focus on front guard when enter focus zone
   */
  focusFirstItemOnMount: PropTypes.bool,
  /**
   * use this flag to enable skip focus action on Mount or update
   * example scenario for this flag: popup editor need this flag to do flag outside
   * As only the popup editor know the timing to show and do focus
   */
  skipFocusOnMount: PropTypes.bool,
  /**
   * function to customize anchor outside
   */
  getAnchor: PropTypes.func,
};

FocusLock.defaultProps = {
  className: 'react-focus-lock',
  onExit: noop,
  isControlledFocusLock: false,
  isFocusLock: false,
  lockRegionAriaLabel: '',
  focusFirstItemOnMount: false,
  skipFocusOnMount: false,
  getAnchor: null,
};
